// service/Hooks/Titres/usePrivateTransaction.js
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import useSelectedDomain from '../useSelectedDomain';
import { createPrivateTransaction } from '../../api/TransfertTitre/PrivateTransactionService';

export const usePrivateTransaction = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selectedDomain = useSelectedDomain();

  const submitTransaction = useCallback(async (transactionData) => {
    if (!selectedDomain?.id) {
      toast.error('Veuillez sélectionner un domaine');
      return { success: false, error: 'DomainId manquant' };
    }

    setLoading(true);
    setError(null);

    try {
      const completeData = {
        ...transactionData,
        domainId: selectedDomain.id
      };

      // console.log(' Données envoyées:', {
      //   ...completeData,
      //   documents: completeData.documents?.map(f => ({
      //     name: f.name,
      //     size: f.size,
      //     type: f.type
      //   }))
      // });
      
      const response = await createPrivateTransaction(completeData);
      
      // console.log(' Réponse API:', response);
      
      if (response.success) {
        toast.success(response.message || 'Transaction créée avec succès', {
          duration: 5000,
          description: `Référence: ${response.data?.transactionReference || ''}`
        });
        
        return { 
          success: true, 
          error: null,
          data: response
        };
      } else {
        const errorMsg = response?.message || 'Erreur lors de la transaction';
        toast.error(errorMsg, { duration: 5000 });
        
        return { 
          success: false, 
          error: errorMsg,
          data: response
        };
      }
    } catch (err) {
      // console.error(' Erreur détaillée:', err);
      const errorMessage = err.response?.data?.errors 
        ? Object.values(err.response.data.errors).flat().join(', ')
        : err.message || 'Erreur lors de la création de la transaction';
      
      setError(errorMessage);
      toast.error(errorMessage, { duration: 5000 });
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [selectedDomain]);

  const validateTransaction = useCallback((transactionData) => {
    const errors = {};

    // console.log(' Validation des données:', {
    //   ...transactionData,
    //   documents: transactionData.documents?.length || 0
    // });

    // Validations de base
    if (!transactionData.securityId) {
      errors.securityId = 'Le titre est requis';
    }

    if (!transactionData.quantity || transactionData.quantity <= 0) {
      errors.quantity = 'La quantité doit être positive';
    }

    if (!transactionData.transactionType) {
      errors.transactionType = 'Le type de transaction est requis';
    }

    if (!transactionData.accountCategory) {
      errors.accountCategory = 'La catégorie de compte est requise';
    }

    // Validation selon le type de transaction
    const transactionType = transactionData.transactionType;
    
    // Transactions qui nécessitent un compte source
    const needsFromAccount = ['Negociation', 'Mutation', 'CessionOnereuse', 
                              'Livraison', 'TransfertTiers', 'TransfertDossiers',
                              'Annulation', 'Conversion', 'Sequestre', 
                              'DetachementDividende', 'ExerciceDroits', 'Administration'];
    
    if (needsFromAccount.includes(transactionType) && !transactionData.fromAccountId) {
      errors.fromAccountId = 'Le compte source est requis pour ce type de transaction';
    }

    // Transactions qui nécessitent un compte destination
    const needsToAccount = ['Negociation', 'Mutation', 'CessionOnereuse', 
                            'Livraison', 'TransfertTiers', 'TransfertDossiers',
                            'Souscription', 'Antecedent'];
    
    if (needsToAccount.includes(transactionType) && !transactionData.toAccountId) {
      errors.toAccountId = 'Le compte destination est requis pour ce type de transaction';
    }

    // Transactions qui nécessitent un titre de conversion
    if ((transactionType === 'Conversion' || transactionType === 'ExerciceDroits') 
        && !transactionData.conversionSecurityId) {
      errors.conversionSecurityId = 'Le titre de conversion est requis';
    }

    // Validation des dates (seulement si elles sont fournies)
    if (transactionData.dateOperation) {
      const operationDate = new Date(transactionData.dateOperation);
      if (operationDate > new Date()) {
        errors.dateOperation = 'La date d\'opération ne peut pas être dans le futur';
      }
    }

    if (transactionData.dateCredit && transactionData.dateOperation) {
      const creditDate = new Date(transactionData.dateCredit);
      const operationDate = new Date(transactionData.dateOperation);
      if (creditDate < operationDate) {
        errors.dateCredit = 'La date de crédit ne peut pas être antérieure à la date d\'opération';
      }
    }

    // console.log('🔍 Résultat validation:', Object.keys(errors).length === 0 ? ' Valide' : ' Erreurs trouvées', errors);
    
    return errors;
  }, []);

  return {
    loading,
    error,
    submitTransaction,
    validateTransaction
  };
};